import { useSuspenseQuery } from "@apollo/client/react";
import { parseISO } from "date-fns";
import { Check, Pencil } from "lucide-react";
import { type ReactNode, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MutationRefetchQueries } from "~/common/types";
import { DetailLoading } from "~/components/loading";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "~/components/ui";
import { useDipLifecycle } from "~/features/dataImportProjects/context";
import {
  GetEvidenceSetDetailDocument,
  type GetEvidenceSetDetailQuery,
  type GetEvidenceSetDetailQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useDipResourceMutationGuard } from "~/features/dataImportProjects/hooks/useDipResourceMutationGuard";
import { useTenant } from "~/features/tenant/context";
import { cn } from "~/lib/utils";

import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";
import { EVIDENCE_SET_EDIT_FORM_ID, EvidenceSetEditForm } from "./EvidenceSetEditForm";
import { EvidenceSetEvidencesTable } from "./EvidenceSetEvidencesTable";

type EvidenceSet = GetEvidenceSetDetailQuery["evidenceSets"][number];
type EvidenceSetDetailTab = "info" | "evidences";

type EvidenceSetDetailPanelProps = {
  importProjectId: string;
  evidenceSetId: string;
  initialTab?: EvidenceSetDetailTab;
  mode?: "peek" | "page";
};

type MetadataRowProps = {
  label: string;
  children: ReactNode;
};

function MetadataRow({ label, children }: MetadataRowProps) {
  return (
    <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3">
      <dt className="text-12-regular text-fg-3">{label}</dt>
      <dd className="min-w-0 text-14-regular text-fg-1">{children}</dd>
    </div>
  );
}

function formatDate(t: ReturnType<typeof useTranslation>["t"], iso: string | null | undefined) {
  return iso ? t("date.short", { date: parseISO(iso) }) : "-";
}

function EvidenceSetSchemaSummary({ evidenceSet }: { evidenceSet: EvidenceSet }) {
  const { t } = useTranslation();
  const schemas = evidenceSet.evidenceSetPropertySchemaSet?.propertySchemaSet.propertySchemaAssociations ?? [];

  if (schemas.length === 0) {
    return <p className="text-12-regular text-fg-3">-</p>;
  }

  return (
    <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
      <table className="w-full min-w-[760px] border-collapse text-12-regular">
        <thead className="bg-bg-subtle text-fg-2">
          <tr>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.common.position")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.common.name")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.propertySchema.propertyTypeId")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-center font-semibold">
              {t("model.propertySchema.required")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-center font-semibold">
              {t("model.propertySchema.unique")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.propertySchemaAssociation.propertyAttributeTypeId")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.common.description")}
            </th>
          </tr>
        </thead>
        <tbody>
          {[...schemas]
            .sort((a, b) => a.position - b.position)
            .map((schema) => (
              <tr key={schema.id} className="border-t border-border-default first:border-t-0">
                <td className="px-3 py-2 text-fg-2">{schema.position}</td>
                <td className="px-3 py-2 text-fg-1">{schema.name}</td>
                <td className="px-3 py-2 text-fg-2">{schema.propertySchema.propertyTypeId}</td>
                <td className="px-3 py-2 text-center">
                  {schema.required ? (
                    <Badge tone="primary">
                      <Check className="size-3" />
                    </Badge>
                  ) : (
                    <span className="text-fg-3">-</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {schema.unique ? (
                    <Badge tone="primary">
                      <Check className="size-3" />
                    </Badge>
                  ) : (
                    <span className="text-fg-3">-</span>
                  )}
                </td>
                <td className="px-3 py-2 text-fg-2">{schema.propertyAttributeTypeId ?? "-"}</td>
                <td className="px-3 py-2 text-fg-2">{schema.description || "-"}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function EvidenceSetInfoPanel({ evidenceSet }: { evidenceSet: EvidenceSet }) {
  const { t } = useTranslation();

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.15fr)]">
      <section>
        <dl className="grid gap-3">
          <MetadataRow label={t("model.common.name")}>{evidenceSet.name}</MetadataRow>
          {evidenceSet.description ? (
            <MetadataRow label={t("model.common.description")}>{evidenceSet.description}</MetadataRow>
          ) : null}
          <MetadataRow label={t("model.dataImportProject.modelName")}>
            {evidenceSet.evidenceSetImportProjects.length > 0
              ? evidenceSet.evidenceSetImportProjects.map((project) => project.dataImportProjectId).join(", ")
              : "-"}
          </MetadataRow>
          {evidenceSet.deactivatedAt ? (
            <MetadataRow label={t("model.common.deactivatedAt")}>
              <span className="text-score-c">{formatDate(t, evidenceSet.deactivatedAt)}</span>
            </MetadataRow>
          ) : null}
        </dl>
      </section>

      <section className="space-y-2">
        <h3 className="text-14-bold text-fg-1">{t("model.propertySchema.modelNames")}</h3>
        <EvidenceSetSchemaSummary evidenceSet={evidenceSet} />
      </section>
    </div>
  );
}

function EvidenceSetEditBridgeDialog({
  open,
  onOpenChange,
  evidenceSetId,
  refetchQueriesOnEdit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  evidenceSetId: string;
  refetchQueriesOnEdit: MutationRefetchQueries;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
            <div>
              <DialogTitle>
                {t("ui.edit")} {t("model.evidenceSet.modelName")}
              </DialogTitle>
              <p className="mt-1 text-12-regular text-fg-3">
                {t("feature.dataImportProjects.v2.temporaryLegacyBridge", {
                  defaultValue: "Temporary legacy component bridge",
                })}
              </p>
            </div>
            <Button type="submit" form={EVIDENCE_SET_EDIT_FORM_ID} size="sm">
              {t("ui.save")}
            </Button>
          </div>
        </DialogHeader>
        <div className="min-w-[560px]">
          <Suspense fallback={<DetailLoading />}>
            <EvidenceSetEditForm
              key={evidenceSetId}
              evidenceSetId={evidenceSetId}
              onSubmit={() => onOpenChange(false)}
              refetchQueriesOnEdit={refetchQueriesOnEdit}
            />
          </Suspense>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EvidenceSetDetailPanel({
  importProjectId,
  evidenceSetId,
  initialTab = "info",
  mode = "peek",
}: EvidenceSetDetailPanelProps) {
  const { t } = useTranslation();
  const { isReadOnly, projectId } = useDipLifecycle();
  const { canExecuteMutation, setAuthorized } = useDipResourceMutationGuard(
    `evidence-set:${importProjectId}:${evidenceSetId}`,
  );
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const [tab, setTab] = useState<EvidenceSetDetailTab>(initialTab);
  const [openEditForm, setOpenEditForm] = useState(false);

  useEffect(() => {
    if (isReadOnly) setOpenEditForm(false);
  }, [isReadOnly]);
  const { data } = useSuspenseQuery<GetEvidenceSetDetailQuery, GetEvidenceSetDetailQueryVariables>(
    GetEvidenceSetDetailDocument,
    {
      variables: { tenantId, id: evidenceSetId },
    },
  );

  const evidenceSet = data.evidenceSets[0];
  const isAuthorized =
    projectId === importProjectId &&
    Boolean(
      evidenceSet?.evidenceSetImportProjects.some(({ dataImportProjectId }) => dataImportProjectId === projectId),
    );
  setAuthorized(isAuthorized);
  if (!evidenceSet || !isAuthorized) {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  const detailRefetchQueries = [{ query: GetEvidenceSetDetailDocument, variables: { tenantId, id: evidenceSet.id } }];

  return (
    <div className={cn("space-y-4", mode === "page" && "mx-auto max-w-[var(--content-max)] p-8")}>
      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={cn("text-fg-1", mode === "page" ? "text-dewey-title" : "text-[18px] font-semibold")}>
                {evidenceSet.name}
              </h2>
              {evidenceSet.deactivatedAt ? <Badge tone="status-draft">{t("ui.deactivated")}</Badge> : null}
            </div>
            <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-12-regular text-fg-3">
              <div className="flex gap-1.5">
                <dt>{t("model.common.id")}:</dt>
                <dd className="font-mono">{evidenceSet.id}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.createdAt")}:</dt>
                <dd>{formatDate(t, evidenceSet.createdAt)}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.updatedAt")}:</dt>
                <dd>{formatDate(t, evidenceSet.updatedAt)}</dd>
              </div>
            </dl>
          </div>

          <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.edit")}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isReadOnly}
              onClick={() => {
                if (!canExecuteMutation()) return;
                setOpenEditForm(true);
              }}
            >
              <Pencil className="size-3.5" />
              {t("ui.edit")}
            </Button>
          </DipReadOnlyTooltip>
        </div>
      </section>

      <Tabs value={tab} onValueChange={(value) => setTab(value as EvidenceSetDetailTab)}>
        <TabsList>
          <TabsTrigger value="info">{t("ui.info")}</TabsTrigger>
          <TabsTrigger value="evidences">{t("model.evidence.modelNames")}</TabsTrigger>
        </TabsList>
        <TabsContent value="info">
          <EvidenceSetInfoPanel evidenceSet={evidenceSet} />
        </TabsContent>
        <TabsContent value="evidences">
          <EvidenceSetEvidencesTable evidenceSetId={evidenceSet.id} />
        </TabsContent>
      </Tabs>

      {!isReadOnly ? (
        <EvidenceSetEditBridgeDialog
          open={openEditForm}
          onOpenChange={(open) => {
            if (!open || canExecuteMutation()) setOpenEditForm(open);
          }}
          evidenceSetId={evidenceSet.id}
          refetchQueriesOnEdit={detailRefetchQueries}
        />
      ) : null}
    </div>
  );
}
